<template>
  <b-card>
    <TitleTable titleTable="Empresas" iconTable="fas fa-building" />
    <p class="text-muted">Altas, bajas y cupos. Permiso membership-company.manage.</p>
    <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = null">{{ errorMessage }}</b-alert>
    <b-alert v-if="pendingInvoiceUrl" show variant="warning">
      Cobro pendiente.
      <b-button size="sm" class="ml-2" variant="warning" :href="pendingInvoiceUrl" target="_blank" rel="noopener">Pagar</b-button>
    </b-alert>
    <b-row class="mb-3">
      <b-col md="6">
        <b-form-group label="Empresa" label-for="office-company">
          <b-form-select id="office-company" v-model="selectedId" :options="companyOptions" @change="loadDetail" />
        </b-form-group>
      </b-col>
    </b-row>
    <div v-if="detail">
      <p><strong>{{ companyName }}</strong> <span v-if="adminName" class="text-muted">· admin {{ adminName }}</span></p>
      <p>Cupos {{ seats.used || 0 }}/{{ seats.total || 0 }}. Libres {{ seats.free || 0 }}. Mínimo {{ seats.minimum || 5 }}.</p>
      <b-row class="mb-3">
        <b-col md="3">
          <b-form-input v-model.number="seatQuantity" type="number" :min="seatFloor" />
        </b-col>
        <b-col md="3">
          <b-button variant="outline-secondary" @click="reduceSeats">Reducir cupos</b-button>
        </b-col>
      </b-row>
      <b-table :items="employees" :fields="fields" small responsive show-empty empty-text="Sin empleados.">
        <template #cell(person)="data">{{ data.item.name }} {{ data.item.lastname }}</template>
        <template #cell(actions)="data">
          <b-button v-if="!data.item.removed_at" size="sm" variant="outline-danger" @click="remove(data.item)">Baja</b-button>
        </template>
      </b-table>
      <h5 class="mt-3">Alta de empleado</h5>
      <b-row>
        <b-col md="3" class="mb-2"><b-form-input v-model.trim="employee.name" placeholder="Nombre" /></b-col>
        <b-col md="3" class="mb-2"><b-form-input v-model.trim="employee.lastname" placeholder="Apellido" /></b-col>
        <b-col md="3" class="mb-2"><b-form-input v-model="employee.date_birth" placeholder="MM-DD-YYYY" /></b-col>
        <b-col md="3" class="mb-2"><b-form-input v-model.trim="employee.phone" placeholder="Teléfono" /></b-col>
        <b-col md="4" class="mb-2"><b-form-input v-model.trim="employee.email" placeholder="Correo" /></b-col>
      </b-row>
      <b-button variant="primary" :disabled="acting" @click="add">Dar de alta</b-button>
    </div>
  </b-card>
</template>

<script>
import { BAlert, BButton, BCard, BCol, BFormGroup, BFormInput, BFormSelect, BRow, BTable } from 'bootstrap-vue'
import TitleTable from '@/components/TitleTable'
import companyApi from '@/core/services/api/admin/consults/membershipCompany'
import { membershipApiMessage } from '@/core/utils/membershipAdmin'
import { companyOf, unwrapList, unwrapRecord } from '@/core/utils/membershipFamily'
import { isValidDateBirthMdy } from '@/view/pages/landings/utils/registerUserPlanHelpers'

/** Backoffice de planes Empresas. */
export default {
  name: 'MembershipCompanyOffice',
  components: { BAlert, BButton, BCard, BCol, BFormGroup, BFormInput, BFormSelect, BRow, BTable, TitleTable },
  data() {
    return {
      companies: [],
      selectedId: null,
      detail: null,
      acting: false,
      errorMessage: null,
      pendingInvoiceUrl: null,
      seatQuantity: 5,
      employee: { name: '', lastname: '', date_birth: '', phone: '', email: '' },
      fields: [
        { key: 'membership_id', label: 'Membresía' },
        { key: 'person', label: 'Empleado' },
        { key: 'email', label: 'Correo' },
        { key: 'pay_status', label: 'Pago' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    companyOptions() {
      return [{ value: null, text: 'Elige una empresa' }].concat(this.companies.map((item) => {
        const company = companyOf(item)
        return { value: company.id, text: company.name || `Empresa ${company.id}` }
      }))
    },
    companyName() {
      return (this.detail && this.detail.company && this.detail.company.name) || ''
    },
    adminName() {
      // El detalle no trae admin; viene en la fila de GET /api/companies.
      const row = this.companies.find((item) => companyOf(item).id === this.selectedId)
      const admin = (row && row.admin) || (this.detail && this.detail.admin)
      if (!admin) return ''
      return [admin.name, admin.lastname].filter(Boolean).join(' ')
    },
    seats() {
      return (this.detail && this.detail.seats) || {}
    },
    employees() {
      return (this.detail && this.detail.employees) || []
    },
    seatFloor() {
      const minimum = Number(this.seats.minimum)
      return Number.isFinite(minimum) ? minimum : 5
    },
  },
  mounted() {
    this.loadList()
  },
  methods: {
    async loadList() {
      try {
        const response = await companyApi.list()
        this.companies = unwrapList(response)
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudieron cargar las empresas.')
      }
    },
    async loadDetail() {
      if (!this.selectedId) {
        this.detail = null
        return
      }
      this.errorMessage = null
      try {
        const response = await companyApi.detail(this.selectedId)
        this.detail = unwrapRecord(response)
      } catch (error) {
        this.detail = null
        this.errorMessage = membershipApiMessage(error, 'No se pudo cargar la empresa.')
      }
    },
    async add() {
      if (!this.employee.name || !this.employee.lastname || !isValidDateBirthMdy(this.employee.date_birth)) {
        this.errorMessage = 'Nombre, apellido y fecha MM-DD-YYYY son obligatorios.'
        return
      }
      this.acting = true
      this.pendingInvoiceUrl = null
      try {
        const payload = {
          name: this.employee.name,
          lastname: this.employee.lastname,
          date_birth: this.employee.date_birth,
        }
        if (this.employee.phone) payload.phone = this.employee.phone
        if (this.employee.email) payload.email = this.employee.email
        const response = await companyApi.addEmployee(this.selectedId, payload)
        const record = unwrapRecord(response) || {}
        if (record.state === 'pending_payment') this.pendingInvoiceUrl = record.pending_invoice_url
        this.employee = { name: '', lastname: '', date_birth: '', phone: '', email: '' }
        await this.loadDetail()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo dar de alta al empleado.')
      } finally {
        this.acting = false
      }
    },
    async remove(item) {
      this.acting = true
      try {
        await companyApi.removeEmployee(this.selectedId, item.membership_id)
        await this.loadDetail()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudo dar de baja al empleado.')
      } finally {
        this.acting = false
      }
    },
    async reduceSeats() {
      const quantity = Number(this.seatQuantity)
      const total = Number(this.seats.total)
      if (!Number.isInteger(quantity) || quantity < this.seatFloor || (Number.isFinite(total) && quantity >= total)) {
        this.errorMessage = `Solo se pueden reducir cupos, con piso ${this.seatFloor}.`
        return
      }
      this.acting = true
      try {
        await companyApi.updateSeats(this.selectedId, quantity)
        await this.loadDetail()
      } catch (error) {
        this.errorMessage = membershipApiMessage(error, 'No se pudieron reducir los cupos.')
      } finally {
        this.acting = false
      }
    },
  },
}
</script>
